import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const checkPaymentStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPaymentInfo(data);
      setStatus(data.payment_status === 'paid' ? 'success' : 'pending');
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    }
  }, [API_URL, sessionId, token]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, checkPaymentStatus]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold mb-2">Verification du paiement...</h2>
              <p className="text-muted-foreground">Veuillez patienter</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Paiement reussi !</h2>
              <p className="text-muted-foreground mb-6">
                Merci pour votre paiement de {paymentInfo?.amount}€
              </p>
              <div className="space-y-3">
                <Link to="/client/invoices">
                  <Button className="w-full btn-primary">Voir mes factures</Button>
                </Link>
                <Link to="/client">
                  <Button variant="outline" className="w-full">Retour au tableau de bord</Button>
                </Link>
              </div>
            </>
          )}
          
          {status === 'pending' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-yellow-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Paiement en cours...</h2>
              <p className="text-muted-foreground mb-6">
                Votre paiement est en cours de traitement
              </p>
              <Link to="/client">
                <Button className="w-full">Retour au tableau de bord</Button>
              </Link>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Erreur</h2>
              <p className="text-muted-foreground mb-6">
                Une erreur est survenue lors de la verification du paiement
              </p>
              <Link to="/client">
                <Button className="w-full">Retour au tableau de bord</Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
